import type {
  ScriptSection,
  ParsedEmail,
  ParsedSocialPost,
  LandingPageContent,
} from '../types/results';
import { escapeHtml, textToParagraphs, type RichClipboardPayload } from './clipboard';

// ─── Script ──────────────────────────────────────────────────────────────────

export function formatScriptForCopy(
  sections: ScriptSection[],
  fullScriptFallback?: string,
): RichClipboardPayload {
  if (sections.length === 0) {
    const text = (fullScriptFallback || '').trim();
    return {
      html: `<div>${textToParagraphs(text)}</div>`,
      text,
    };
  }

  const htmlBlocks = sections.map((sec, idx) => {
    const timeLine = sec.timeEstimate
      ? `<p style="margin:4px 0 12px 0;color:#555;font-size:13px;"><em>${escapeHtml(sec.timeEstimate)}</em></p>`
      : '';
    return [
      `<h2 style="margin:${idx === 0 ? 0 : 24}px 0 8px 0;font-size:20px;"><strong>${escapeHtml(sec.title)}</strong></h2>`,
      timeLine,
      textToParagraphs(sec.content),
    ].join('');
  });

  const html = `<div style="font-family:Arial,sans-serif;line-height:1.55;">${htmlBlocks.join('<br>')}</div>`;

  const textBlocks = sections.map((sec) => {
    const head = sec.timeEstimate
      ? `${sec.title.toUpperCase()}  (${sec.timeEstimate})`
      : sec.title.toUpperCase();
    return `${head}\n\n${sec.content.trim()}`;
  });
  const text = textBlocks.join('\n\n---\n\n');

  return { html, text };
}

// ─── Marketing / Follow-Up Emails ───────────────────────────────────────────

export function formatEmailsForCopy(
  emails: ParsedEmail[],
  kind: 'marketing' | 'followup',
): RichClipboardPayload {
  if (emails.length === 0) return { html: '', text: '' };

  const prefix = kind === 'marketing' ? 'EMAIL' : 'FOLLOW-UP';

  const htmlBlocks = emails.map((email, idx) => {
    const label = `${prefix} ${email.number} — ${email.sendDay || ''}`.trim().replace(/—\s*$/, '');
    const phaseLine = email.phase || email.angle
      ? `<p style="margin:0 0 12px 0;color:#666;font-size:13px;"><em>${escapeHtml([email.phase, email.angle].filter(Boolean).join(' · '))}</em></p>`
      : '';
    return [
      `<h2 style="margin:${idx === 0 ? 0 : 28}px 0 4px 0;font-size:18px;"><strong>${escapeHtml(label)}</strong></h2>`,
      phaseLine,
      `<p style="margin:8px 0;"><strong>Subject:</strong> ${escapeHtml(email.subject || '')}</p>`,
      textToParagraphs(email.body || ''),
    ].join('');
  });

  const html = `<div style="font-family:Arial,sans-serif;line-height:1.55;">${htmlBlocks.join('<br>')}</div>`;

  const textBlocks = emails.map((email) => {
    const label = `${prefix} ${email.number} — ${email.sendDay || ''}`.trim().replace(/—\s*$/, '');
    const phase =
      email.phase || email.angle
        ? `(${[email.phase, email.angle].filter(Boolean).join(' · ')})\n`
        : '';
    return `${label.toUpperCase()}\n${phase}\nSubject: ${email.subject || ''}\n\n${(email.body || '').trim()}`;
  });
  const text = textBlocks.join('\n\n---\n\n');

  return { html, text };
}

// ─── Social Posts ───────────────────────────────────────────────────────────

export function formatSocialPostsForCopy(posts: ParsedSocialPost[]): RichClipboardPayload {
  if (posts.length === 0) return { html: '', text: '' };

  const htmlBlocks = posts.map((post, idx) => {
    const label = `POST ${post.number} — ${post.dayLabel || ''}`.trim().replace(/—\s*$/, '');
    const phaseLine = post.phase || post.angle
      ? `<p style="margin:0 0 12px 0;color:#666;font-size:13px;"><em>${escapeHtml([post.phase, post.angle].filter(Boolean).join(' · '))}</em></p>`
      : '';
    return [
      `<h2 style="margin:${idx === 0 ? 0 : 28}px 0 4px 0;font-size:18px;"><strong>${escapeHtml(label)}</strong></h2>`,
      phaseLine,
      textToParagraphs(post.content || ''),
      `<p style="margin:8px 0 0 0;color:#888;font-size:12px;">${post.characterCount.toLocaleString()} characters</p>`,
    ].join('');
  });

  const html = `<div style="font-family:Arial,sans-serif;line-height:1.55;">${htmlBlocks.join('<br>')}</div>`;

  const textBlocks = posts.map((post) => {
    const label = `POST ${post.number} — ${post.dayLabel || ''}`.trim().replace(/—\s*$/, '');
    const phase =
      post.phase || post.angle
        ? `(${[post.phase, post.angle].filter(Boolean).join(' · ')})\n`
        : '';
    return `${label.toUpperCase()}\n${phase}\n${(post.content || '').trim()}\n\n(${post.characterCount.toLocaleString()} chars)`;
  });
  const text = textBlocks.join('\n\n---\n\n');

  return { html, text };
}

// ─── Landing Page ───────────────────────────────────────────────────────────

export function formatLandingPageForCopy(content: LandingPageContent | null): RichClipboardPayload {
  if (!content) return { html: '', text: '' };

  const htmlParts: string[] = [];
  const textParts: string[] = [];

  // Title
  if (content.title) {
    htmlParts.push(
      `<h2 style="margin:0 0 4px 0;font-size:18px;"><strong>Landing Page Title</strong></h2>`,
      `<p style="margin:0 0 16px 0;font-size:16px;"><strong>${escapeHtml(content.title)}</strong></p>`,
    );
    textParts.push(`LANDING PAGE TITLE\n\n${content.title}`);
  }

  // Subtitle
  if (content.subtitle) {
    htmlParts.push(
      `<h2 style="margin:16px 0 4px 0;font-size:18px;"><strong>Subtitle</strong></h2>`,
      `<p style="margin:0 0 16px 0;">${escapeHtml(content.subtitle)}</p>`,
    );
    textParts.push(`SUBTITLE\n\n${content.subtitle}`);
  }

  // Description
  if (content.description) {
    htmlParts.push(
      `<h2 style="margin:16px 0 4px 0;font-size:18px;"><strong>Description</strong></h2>`,
      textToParagraphs(content.description),
    );
    textParts.push(`DESCRIPTION\n\n${content.description.trim()}`);
  }

  // Hooks
  if (content.hooks.length > 0) {
    htmlParts.push(
      `<h2 style="margin:20px 0 8px 0;font-size:18px;"><strong>Key Hooks</strong></h2>`,
      `<ul style="margin:0 0 16px 20px;padding:0;">` +
        content.hooks.map((h) => `<li style="margin:4px 0;">${escapeHtml(h)}</li>`).join('') +
        `</ul>`,
    );
    textParts.push(`KEY HOOKS\n\n${content.hooks.map((h) => `• ${h}`).join('\n')}`);
  }

  // Three Secrets
  if (content.secrets.length > 0) {
    htmlParts.push(
      `<h2 style="margin:24px 0 12px 0;font-size:18px;"><strong>Three Secrets</strong></h2>`,
    );
    textParts.push('THREE SECRETS');

    content.secrets.forEach((secret, idx) => {
      htmlParts.push(
        `<h3 style="margin:16px 0 4px 0;font-size:16px;"><strong>Secret #${idx + 1} — ${escapeHtml(secret.title)}</strong></h3>`,
        textToParagraphs(secret.paragraph),
      );
      textParts.push(`\nSecret #${idx + 1} — ${secret.title}\n\n${secret.paragraph.trim()}`);
    });
  }

  const html = `<div style="font-family:Arial,sans-serif;line-height:1.55;">${htmlParts.join('')}</div>`;
  const text = textParts.join('\n\n---\n\n');

  return { html, text };
}
